import React, { useState } from 'react';
import { Download, Trash2, FileText, FileImage, FileVideo, File as FileIcon } from 'lucide-react';
import ImageLightbox from './ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const iconFor = (type, contentType) => {
  if (type === 'image' || (contentType || '').startsWith('image/')) return FileImage;
  if (type === 'video' || (contentType || '').startsWith('video/')) return FileVideo;
  if ((contentType || '').includes('pdf')) return FileText;
  return FileIcon;
};

const fmtBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const toAbs = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

/**
 * Read-only attachment list with download + optional delete buttons.
 * Props:
 *  - attachments: [{id?, url, name, type?, content_type?, size?, uploaded_by_id?, uploaded_by_name?, uploaded_at?}]
 *  - canDeleteFn(att): (att) => boolean (default () => false)
 *  - onDelete(att): async handler
 *  - compact: boolean — tighter list for use inside chat/comment area
 *  - showPreview: boolean — if image, show small thumbnail
 *  - testIdPrefix: string
 */
const AttachmentList = ({
  attachments = [],
  canDeleteFn = () => false,
  onDelete = null,
  compact = false,
  showPreview = true,
  testIdPrefix = 'attachment',
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  if (!attachments || attachments.length === 0) return null;

  const isImageAttachment = (a) => a.type === 'image' || (a.content_type || '').startsWith('image/');
  const imageAttachments = attachments.filter(isImageAttachment);

  const openLightboxFor = (att) => {
    const idx = imageAttachments.findIndex((x) => x.url === att.url);
    setLightboxIndex(idx >= 0 ? idx : 0);
  };

  return (
    <>
    <ul className={compact ? 'space-y-1' : 'space-y-2'}>
      {attachments.map((a, idx) => {
        const Icon = iconFor(a.type, a.content_type);
        const href = toAbs(a.url);
        const isImage = isImageAttachment(a);
        const key = a.id || `${a.url}-${idx}`;
        return (
          <li
            key={key}
            className={`flex items-center gap-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors ${compact ? 'p-1.5' : 'p-2.5'}`}
            data-testid={`${testIdPrefix}-item-${a.id || idx}`}
          >
            {showPreview && isImage ? (
              <button
                type="button"
                onClick={() => openLightboxFor(a)}
                className="flex-shrink-0 rounded overflow-hidden border border-slate-200 hover:ring-2 hover:ring-indigo-400 transition-all"
                title="Click pentru a deschide"
                data-testid={`${testIdPrefix}-thumb-${a.id || idx}`}
              >
                <img
                  src={href}
                  alt={a.name}
                  className={`${compact ? 'w-8 h-8' : 'w-10 h-10'} object-cover`}
                  loading="lazy"
                />
              </button>
            ) : (
              <Icon className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-indigo-500 flex-shrink-0`} />
            )}
            {isImage ? (
              <button
                type="button"
                onClick={() => openLightboxFor(a)}
                className={`flex-1 min-w-0 text-left font-medium text-slate-800 hover:text-indigo-600 truncate ${compact ? 'text-xs' : 'text-sm'}`}
                title={a.name}
                data-testid={`${testIdPrefix}-open-${a.id || idx}`}
              >
                {a.name}
              </button>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-1 min-w-0 font-medium text-slate-800 hover:text-indigo-600 truncate ${compact ? 'text-xs' : 'text-sm'}`}
                title={a.name}
              >
                {a.name}
              </a>
            )}
            {!compact && (
              <span className="hidden md:inline text-xs text-slate-500 whitespace-nowrap">
                {fmtBytes(a.size)}
                {a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ''}
                {a.uploaded_at ? ` · ${fmtDate(a.uploaded_at)}` : ''}
              </span>
            )}
            <a
              href={href}
              download={a.name}
              className="p-1.5 rounded hover:bg-slate-200 text-slate-500"
              title="Descarca"
              data-testid={`${testIdPrefix}-download-${a.id || idx}`}
            >
              <Download className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
            </a>
            {onDelete && canDeleteFn(a) && (
              <button
                type="button"
                onClick={() => onDelete(a)}
                className="p-1.5 rounded hover:bg-rose-100 text-rose-500"
                title="Sterge"
                data-testid={`${testIdPrefix}-delete-${a.id || idx}`}
              >
                <Trash2 className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
    {lightboxIndex !== null && imageAttachments.length > 0 && (
      <ImageLightbox
        images={imageAttachments}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    )}
    </>
  );
};

export default AttachmentList;
